import { setDisplayCurrency } from "@ccflare/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LoginPage } from "./components/LoginPage";
import { Navigation } from "./components/navigation";
import { QUERY_CONFIG, REFRESH_INTERVALS } from "./constants";
import { AuthProvider, useAuth } from "./contexts/auth-context";
import { ThemeProvider } from "./contexts/theme-context";
import { useFxRate } from "./hooks/queries";
import "./index.css";
import { dashboardRoutes } from "./routes";

const FX_STORAGE_KEY = "ccflare.displayCurrency";

// Hydrate the display currency from the last-known rate before first paint so
// costs don't flash USD then switch to EUR.
try {
	const cached = localStorage.getItem(FX_STORAGE_KEY);
	if (cached) setDisplayCurrency(JSON.parse(cached));
} catch {
	// ignore malformed cache
}

/** Keep the ambient display currency in sync with the server's FX rate. */
function useApplyDisplayCurrency() {
	const { data } = useFxRate();
	useEffect(() => {
		if (!data) return;
		const currency = {
			rate: data.rate,
			code: data.currency,
			symbol: data.symbol,
		};
		setDisplayCurrency(currency);
		try {
			localStorage.setItem(FX_STORAGE_KEY, JSON.stringify(currency));
		} catch {
			// ignore storage failures
		}
	}, [data]);
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchInterval: REFRESH_INTERVALS.default, // Refetch every 30 seconds
			staleTime: QUERY_CONFIG.staleTime, // Consider data stale after 10 seconds
		},
	},
});

function DashboardShell() {
	const location = useLocation();
	const { gatewayMode, user, isLoading } = useAuth();
	useApplyDisplayCurrency();

	const visibleRoutes = gatewayMode
		? dashboardRoutes
		: dashboardRoutes.filter((r) => !r.gatewayOnly);

	const currentRoute =
		visibleRoutes.find((route) => route.path === location.pathname) ||
		visibleRoutes[0];

	// While we're loading gateway status, show nothing to avoid flash
	if (isLoading) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<p className="text-muted-foreground">Loading...</p>
			</div>
		);
	}

	// Gateway mode: show login if not authenticated
	if (gatewayMode && !user) {
		return <LoginPage />;
	}

	return (
		<div className="min-h-screen bg-background">
			<Navigation visibleRoutes={visibleRoutes} />

			{/* Main Content */}
			<main className="lg:pl-64">
				{/* Mobile spacer */}
				<div className="h-16 lg:hidden" />

				{/* Page Content */}
				<div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
					{/* Page Header */}
					<div className="mb-8">
						<h1 className="text-3xl font-bold gradient-text">
							{currentRoute?.title ?? ""}
						</h1>
						<p className="text-muted-foreground mt-2">
							{currentRoute?.subtitle ?? ""}
						</p>
					</div>

					{/* Tab Content */}
					<div className="animate-in fade-in-0 duration-200">
						<Routes>
							{visibleRoutes.map((route) => (
								<Route
									key={route.path}
									path={route.path}
									element={route.element}
								/>
							))}
							<Route path="*" element={<Navigate to="/" replace />} />
						</Routes>
					</div>
				</div>
			</main>
		</div>
	);
}

export function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider>
				<AuthProvider>
					<DashboardShell />
				</AuthProvider>
			</ThemeProvider>
		</QueryClientProvider>
	);
}
